import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormButton,
  Input,
  string,
  validatePassword
} from '@ijoin/components';
import {
  useGetValidateToken,
  usePatchMfaVerify,
  usePostComplete,
  usePostMfaGenerate,
  usePostSetPassword
} from '@ijoin/hooks';
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const mfaResolver = zodResolver(
  z.object({ mfaCode: string({ required: true }).length(6) })
);

const SetupPage = () => {
  const navigate = useNavigate();
  const { returnTo, token } = Route.useSearch();

  const [step, setStep] = useState<'mfa' | 'password'>('password');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  const tokenQuery = useGetValidateToken(
    { token },
    { query: { enabled: !!token, retry: false } }
  );

  const passwordResolver = zodResolver(
    z
      .object({
        confirmPassword: string({ required: true }),
        password: tokenQuery.data
          ? validatePassword([tokenQuery.data.username])
          : string({ required: true })
      })
      .refine(data => data.password === data.confirmPassword, {
        message: 'Passwords must match',
        path: ['confirmPassword']
      })
  );

  const passwordMethods = useForm({
    defaultValues: { confirmPassword: '', password: '' },
    mode: 'all',
    resolver: passwordResolver
  });

  const mfaMethods = useForm({
    defaultValues: { mfaCode: '' },
    mode: 'all',
    resolver: mfaResolver
  });

  const setPasswordMutation = usePostSetPassword();
  const generateMfaMutation = usePostMfaGenerate();
  const verifyMfaMutation = usePatchMfaVerify();
  const completeMutation = usePostComplete();

  const onPasswordSubmit = async (data: {
    confirmPassword: string;
    password: string;
  }) => {
    try {
      await setPasswordMutation.mutateAsync({
        data: { password: data.password, token }
      });
      const qrData = await generateMfaMutation.mutateAsync({
        data: { token }
      });
      setQrCodeDataUrl(qrData.qrCodeDataUrl);
      setStep('mfa');
    } catch {
      // errors handled via mutation state
    }
  };

  const onMfaSubmit = async (data: { mfaCode: string }) => {
    try {
      await verifyMfaMutation.mutateAsync({
        data: { code: data.mfaCode, token }
      });
      await completeMutation.mutateAsync({ data: { token } });

      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }

      navigate({ to: '/login' });
    } catch {
      // errors handled via mutation state
    }
  };

  if (!token || tokenQuery.isError) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ height: '100vh' }}>
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardHeader title='Invalid Invitation' />
          <CardContent>
            <Alert severity='error'>
              This invitation link is invalid or has expired. Please contact
              your administrator for a new invitation.
            </Alert>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', pb: 2, px: 2 }}>
            <Button
              data-testid='goToLogin'
              onClick={() => navigate({ to: '/login' })}
              variant='contained'>
              Go to Login
            </Button>
          </CardActions>
        </Card>
      </Stack>
    );
  }

  if (tokenQuery.isLoading) {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ height: '100vh' }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (step === 'password') {
    return (
      <Stack
        alignItems='center'
        justifyContent='center'
        sx={{ height: '100vh' }}>
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <Form
            methods={passwordMethods}
            noValidate
            onSubmit={passwordMethods.handleSubmit(onPasswordSubmit)}>
            <CardHeader
              subheader='Set your password to get started'
              title={`Welcome, ${tokenQuery.data?.username}`}
            />
            <CardContent>
              <Stack spacing={2}>
                <Input
                  data-testid='setupPassword'
                  format='password'
                  label='Password'
                  name='password'
                />
                <Input
                  data-testid='setupConfirmPassword'
                  format='password'
                  label='Confirm Password'
                  name='confirmPassword'
                />
                {setPasswordMutation.isError && (
                  <Alert severity='error'>
                    Failed to set password. Please try again.
                  </Alert>
                )}
              </Stack>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', pb: 2, px: 2 }}>
              <FormButton
                data-testid='setupContinue'
                disabled={setPasswordMutation.isPending}
                triggerValidation
                variant='contained'>
                Continue
              </FormButton>
            </CardActions>
          </Form>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack alignItems='center' justifyContent='center' sx={{ height: '100vh' }}>
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <Form
          methods={mfaMethods}
          noValidate
          onSubmit={mfaMethods.handleSubmit(onMfaSubmit)}>
          <CardHeader
            subheader='Scan the QR code with your authenticator app'
            title='Set Up Two-Factor Authentication'
          />
          <CardContent>
            <Stack alignItems='center' spacing={2}>
              {qrCodeDataUrl && (
                <img
                  alt='QR Code'
                  src={qrCodeDataUrl}
                  style={{ height: 200, width: 200 }}
                />
              )}
              <Typography color='text.secondary' variant='body2'>
                Enter the 6-digit code from your authenticator app
              </Typography>
              <Input
                data-testid='setupMfaCode'
                format='string'
                fullWidth
                label='Verification Code'
                name='mfaCode'
              />
              {verifyMfaMutation.isError && (
                <Alert severity='error'>
                  Invalid verification code. Please try again.
                </Alert>
              )}
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: 'flex-end', pb: 2, px: 2 }}>
            <FormButton
              data-testid='setupVerify'
              disabled={verifyMfaMutation.isPending}
              triggerValidation
              variant='contained'>
              Verify & Complete
            </FormButton>
          </CardActions>
        </Form>
      </Card>
    </Stack>
  );
};

export const Route = createFileRoute('/setup')({
  component: SetupPage,
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: (search.returnTo as string) || '',
    token: (search.token as string) || ''
  })
});
